import type { ApiRequestOptions } from '../types/api.js';
import { getMarketingLoginUrl, isMarketingAuthPage } from '../utils/marketingSite.js';
import { clearAuthStatusCache } from './authStatusCache.js';

export type { ApiRequestOptions } from '../types/api.js';

/**
 * Error thrown for any non-OK HTTP response. Carries the HTTP status and the
 * parsed error body (when the server sent JSON) so callers can branch on
 * status (e.g. 401/403) and surface server-provided messages instead of a
 * generic "Request failed" string.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? deriveMessage(status, body));
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    // Restore prototype chain for instanceof across transpilation targets.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

function deriveMessage(status: number, body: unknown): string {
  if (body && typeof body === 'object' && 'error' in body) {
    const candidate = (body as { error?: unknown }).error;
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }
  if (body && typeof body === 'object' && 'message' in body) {
    const candidate = (body as { message?: unknown }).message;
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }
  return `Request failed with ${status}`;
}

// The auth-status endpoint reports logged-out state as a normal response (or a
// 401). Redirecting on a 401 from THIS path would create a redirect loop, since
// the bootstrap flow calls it precisely to discover the logged-out state and
// then redirects itself. Keep this in sync with authService.getAuthStatus().
export const AUTH_STATUS_PATH = '/api/auth/status';

export function isAuthStatusPath(path: string): boolean {
  return path === AUTH_STATUS_PATH || path.endsWith(AUTH_STATUS_PATH);
}

export function redirectToLogin(): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (isMarketingAuthPage()) {
    return;
  }
  window.location.href = getMarketingLoginUrl();
}

/**
 * When a protected API returns 401, clear cached auth and send the user to login.
 * Returns true when a redirect was triggered (callers should still throw/stop).
 */
export function handleUnauthorizedResponse(status: number, requestPath: string): boolean {
  if (status !== 401 || isAuthStatusPath(requestPath)) {
    return false;
  }
  clearAuthStatusCache();
  redirectToLogin();
  return true;
}

function clientTimezoneHeaders(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timeZone ? { 'X-Client-Timezone': timeZone } : {};
  } catch {
    return {};
  }
}

function chatClientHeaders(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {};
  }
  const headers: Record<string, string> = {
    'x-nexvill-client': 'web',
    'x-nexvill-chat-origin': window.location.origin.replace(/\/+$/, ''),
  };
  return headers;
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const requestUrl = path.startsWith('http')
    ? path
    : new URL(path, typeof window !== 'undefined' ? window.location.origin : 'http://localhost').toString();

  const response = await fetch(requestUrl, {
    credentials: 'include',
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...clientTimezoneHeaders(),
      ...chatClientHeaders(),
      ...options.headers,
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    const body = await readErrorBody(response);

    handleUnauthorizedResponse(response.status, path);

    throw new ApiError(response.status, body);
  }

  return (await response.json()) as T;
}

async function readErrorBody(response: Response): Promise<unknown> {
  try {
    const text = await response.text();
    if (!text) {
      return null;
    }
    try {
      return JSON.parse(text);
    } catch {
      // Non-JSON error body (e.g. HTML error page); return the raw text.
      return text;
    }
  } catch {
    return null;
  }
}
